import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Textarea } from "../components/ui/textarea";
import { 
  Brain, AlertTriangle, CheckCircle2, Send, Clock, RefreshCw,
  MessageSquare, Users, Zap, FileText, Activity, Heart,
  TrendingUp, Eye, Edit2
} from 'lucide-react';
import { useToast } from "../hooks/use-toast";

const API = process.env.REACT_APP_BACKEND_URL;

// Message triage categories
const TRIAGE_CATEGORIES = {
  green: { label: 'Informational', color: 'green', icon: CheckCircle2, action: 'Auto-respond' },
  yellow: { label: 'Monitor', color: 'yellow', icon: Eye, action: 'Follow-up' },
  red: { label: 'Immediate', color: 'red', icon: AlertTriangle, action: 'Urgent' }
};

const SmartTriageDashboard = () => {
  const [messages, setMessages] = useState([]);
  const [draftResponses, setDraftResponses] = useState({});
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('inbox');
  const [selectedMessage, setSelectedMessage] = useState(null);
  const { toast } = useToast();

  const token = localStorage.getItem('token');

  const fetchMessages = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/phase38-44/triage/inbox`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setMessages(data.messages || []);
        setDraftResponses(data.draft_responses || {});
        setStats(data.stats);
      }
    } catch (err) {
      console.error('Error fetching messages:', err);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchMessages();
  }, [fetchMessages]);

  // Approve and send auto-drafted response
  const approveResponse = async (messageId) => {
    try {
      const res = await fetch(`${API}/api/phase38-44/triage/approve-response/${messageId}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (res.ok) {
        toast({ title: "Response Sent", description: "Auto-drafted response has been sent." });
        fetchMessages();
      }
    } catch (err) {
      toast({ title: "Error", description: "Failed to send response", variant: "destructive" });
    }
  };

  // Edit and send custom response
  const sendCustomResponse = async (messageId, response) => {
    try {
      const res = await fetch(`${API}/api/phase38-44/triage/custom-response/${messageId}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ response })
      });

      if (res.ok) {
        toast({ title: "Response Sent", description: "Your custom response has been sent." });
        setSelectedMessage(null);
        fetchMessages();
      }
    } catch (err) {
      toast({ title: "Error", description: "Failed to send response", variant: "destructive" });
    }
  };

  // Escalate message
  const escalateMessage = async (messageId, reason) => {
    try {
      const res = await fetch(`${API}/api/phase38-44/triage/escalate/${messageId}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ reason })
      });

      if (res.ok) {
        toast({ 
          title: "Escalated", 
          description: "Message has been escalated to the care team lead.",
          variant: "warning" 
        });
        fetchMessages();
      }
    } catch (err) {
      toast({ title: "Error", description: "Failed to escalate", variant: "destructive" });
    }
  };

  const getTriageInfo = (category) => TRIAGE_CATEGORIES[category] || TRIAGE_CATEGORIES.yellow;

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <RefreshCw className="h-8 w-8 animate-spin text-teal-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6 p-4 md:p-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
            <Brain className="h-7 w-7 text-teal-600" />
            Smart Triage Dashboard
          </h1>
          <p className="text-gray-600">AI-powered message categorization & auto-responses</p>
        </div>
        <Badge className="bg-teal-500 text-lg px-4 py-2">
          ~2 hours saved today
        </Badge>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-green-50 border-green-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-green-600 font-medium">Green (Auto)</p>
                <p className="text-3xl font-bold text-green-700">{stats?.green || 0}</p>
              </div>
              <CheckCircle2 className="h-10 w-10 text-green-500" />
            </div>
            <p className="text-xs text-green-600 mt-2">Ready to send</p>
          </CardContent>
        </Card>

        <Card className="bg-yellow-50 border-yellow-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-yellow-600 font-medium">Yellow (Review)</p>
                <p className="text-3xl font-bold text-yellow-700">{stats?.yellow || 0}</p>
              </div>
              <Eye className="h-10 w-10 text-yellow-500" />
            </div>
            <p className="text-xs text-yellow-600 mt-2">Needs follow-up</p>
          </CardContent>
        </Card>

        <Card className="bg-red-50 border-red-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-red-600 font-medium">Red (Urgent)</p>
                <p className="text-3xl font-bold text-red-700">{stats?.red || 0}</p>
              </div>
              <AlertTriangle className="h-10 w-10 text-red-500" />
            </div>
            <p className="text-xs text-red-600 mt-2">Immediate action</p>
          </CardContent>
        </Card>

        <Card className="bg-blue-50 border-blue-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-blue-600 font-medium">Time Saved</p>
                <p className="text-3xl font-bold text-blue-700">{stats?.hours_saved || 2}h</p>
              </div>
              <Clock className="h-10 w-10 text-blue-500" />
            </div>
            <p className="text-xs text-blue-600 mt-2">Today's productivity</p>
          </CardContent>
        </Card>
      </div>

      {/* Bulk Action Banner */}
      {(stats?.green || 0) > 0 && (
        <Card className="bg-gradient-to-r from-green-50 to-teal-50 border-green-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Zap className="h-6 w-6 text-green-600" />
                <div>
                  <h3 className="font-semibold text-green-900">
                    {stats?.green || 0} messages ready for auto-response
                  </h3>
                  <p className="text-sm text-green-700">
                    AI has drafted responses for all green-category messages
                  </p>
                </div>
              </div>
              <Button className="bg-green-600 hover:bg-green-700">
                <Send className="h-4 w-4 mr-2" />
                Approve All Green
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid grid-cols-4 w-full max-w-lg">
          <TabsTrigger value="inbox">
            Inbox
            {messages.length > 0 && (
              <Badge className="ml-1 bg-gray-500 h-5 px-1.5">{messages.length}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="green">Green</TabsTrigger>
          <TabsTrigger value="yellow">Yellow</TabsTrigger>
          <TabsTrigger value="red">Red</TabsTrigger>
        </TabsList>

        {/* All Messages */}
        <TabsContent value="inbox" className="space-y-4">
          {messages.length > 0 ? (
            <div className="space-y-3">
              {messages.map((msg, i) => {
                const triageInfo = getTriageInfo(msg.triage_category);
                return (
                  <Card key={i} className={`border-l-4 ${
                    msg.triage_category === 'red' ? 'border-l-red-500 bg-red-50' :
                    msg.triage_category === 'yellow' ? 'border-l-yellow-500 bg-yellow-50' :
                    'border-l-green-500 bg-green-50'
                  }`}>
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex items-start gap-3 flex-1">
                          <div className={`p-2 rounded-full ${
                            msg.triage_category === 'red' ? 'bg-red-100' :
                            msg.triage_category === 'yellow' ? 'bg-yellow-100' : 'bg-green-100'
                          }`}>
                            <triageInfo.icon className={`h-5 w-5 ${
                              msg.triage_category === 'red' ? 'text-red-600' :
                              msg.triage_category === 'yellow' ? 'text-yellow-600' : 'text-green-600'
                            }`} />
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <h4 className="font-medium">{msg.sender_name}</h4>
                              <Badge variant="outline">{msg.sender_role}</Badge>
                              <Badge className={
                                msg.triage_category === 'red' ? 'bg-red-500' :
                                msg.triage_category === 'yellow' ? 'bg-yellow-500' : 'bg-green-500'
                              }>
                                {triageInfo.label}
                              </Badge>
                            </div>
                            <p className="text-sm text-gray-700 mb-2">{msg.content}</p>
                            <p className="text-xs text-gray-500">
                              Re: {msg.patient_name} • {new Date(msg.received_at).toLocaleString()}
                            </p>

                            {/* AI Detection Reason */}
                            {msg.triage_reason && (
                              <div className="mt-2 p-2 bg-white/50 rounded text-xs text-gray-600">
                                <span className="font-medium">AI Detection:</span> {msg.triage_reason}
                              </div>
                            )}

                            {/* Auto-drafted Response for Green */}
                            {msg.triage_category === 'green' && draftResponses[msg.id] && (
                              <div className="mt-3 p-3 bg-white rounded-lg border border-green-200">
                                <div className="flex items-center gap-2 mb-2">
                                  <Brain className="h-4 w-4 text-teal-600" />
                                  <span className="text-xs font-medium text-teal-700">AI Draft Response</span>
                                </div>
                                <p className="text-sm text-gray-700">{draftResponses[msg.id]}</p>
                              </div>
                            )}
                          </div>
                        </div>

                        <div className="flex flex-col gap-2 ml-4">
                          {msg.triage_category === 'green' && (
                            <Button 
                              size="sm" 
                              className="bg-green-600 hover:bg-green-700"
                              onClick={() => approveResponse(msg.id)}
                            >
                              <Send className="h-3 w-3 mr-1" />
                              Approve & Send
                            </Button>
                          )}
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => setSelectedMessage(msg)}
                          >
                            <Edit2 className="h-3 w-3 mr-1" />
                            Edit
                          </Button>
                          {msg.triage_category !== 'red' && (
                            <Button 
                              size="sm" 
                              variant="outline"
                              className="border-red-300 text-red-600 hover:bg-red-50"
                              onClick={() => escalateMessage(msg.id, 'Manual escalation')}
                            >
                              Escalate
                            </Button>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          ) : (
            <Card>
              <CardContent className="p-8 text-center">
                <CheckCircle2 className="h-12 w-12 mx-auto text-green-400 mb-3" />
                <h3 className="font-semibold text-gray-600">Inbox Clear!</h3>
                <p className="text-sm text-gray-500">All messages have been triaged</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Green Messages */}
        <TabsContent value="green">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <CheckCircle2 className="h-5 w-5 text-green-600" />
                Informational Messages
              </CardTitle>
              <CardDescription>Auto-draft responses ready for approval</CardDescription>
            </CardHeader>
            <CardContent>
              {messages.filter(m => m.triage_category === 'green').length > 0 ? (
                <div className="space-y-3">
                  {messages.filter(m => m.triage_category === 'green').map((msg, i) => (
                    <div key={i} className="p-4 bg-green-50 rounded-lg border border-green-200">
                      <div className="flex justify-between items-start">
                        <div>
                          <h4 className="font-medium">{msg.sender_name}</h4>
                          <p className="text-sm text-gray-600">{msg.content}</p>
                        </div>
                        <Button 
                          size="sm" 
                          className="bg-green-600"
                          onClick={() => approveResponse(msg.id)}
                        >
                          <Send className="h-3 w-3 mr-1" />
                          Send
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-center text-gray-500 py-6">No green messages</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Yellow Messages */}
        <TabsContent value="yellow">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Eye className="h-5 w-5 text-yellow-600" />
                Monitor / Follow-up
              </CardTitle>
              <CardDescription>These messages need your attention</CardDescription>
            </CardHeader>
            <CardContent>
              {messages.filter(m => m.triage_category === 'yellow').length > 0 ? (
                <div className="space-y-3">
                  {messages.filter(m => m.triage_category === 'yellow').map((msg, i) => (
                    <div key={i} className="p-4 bg-yellow-50 rounded-lg border border-yellow-200">
                      <h4 className="font-medium">{msg.sender_name}</h4>
                      <p className="text-sm text-gray-600">{msg.content}</p>
                      <p className="text-xs text-gray-500 mt-1">Re: {msg.patient_name}</p>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-center text-gray-500 py-6">No yellow messages</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Red Messages */}
        <TabsContent value="red">
          <Card className="border-red-200">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2 text-red-700">
                <AlertTriangle className="h-5 w-5 text-red-600" />
                Immediate Intervention Required
              </CardTitle>
              <CardDescription>These messages require urgent attention</CardDescription>
            </CardHeader>
            <CardContent>
              {messages.filter(m => m.triage_category === 'red').length > 0 ? (
                <div className="space-y-3">
                  {messages.filter(m => m.triage_category === 'red').map((msg, i) => (
                    <div key={i} className="p-4 bg-red-50 rounded-lg border border-red-200">
                      <h4 className="font-medium text-red-900">{msg.sender_name}</h4>
                      <p className="text-sm text-red-800">{msg.content}</p>
                      <p className="text-xs text-red-600 mt-1">Re: {msg.patient_name}</p>
                      <div className="mt-2 flex gap-2">
                        <Button size="sm" className="bg-red-600 hover:bg-red-700">
                          Take Action
                        </Button>
                        <Button size="sm" variant="outline" className="border-red-300">
                          Call Caregiver
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-6">
                  <CheckCircle2 className="h-10 w-10 mx-auto text-green-500 mb-2" />
                  <p className="text-gray-500">No urgent messages</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Edit Response Modal */}
      {selectedMessage && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-lg">
            <CardHeader>
              <CardTitle>Edit Response</CardTitle>
              <CardDescription>To: {selectedMessage.sender_name}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="p-3 bg-gray-50 rounded-lg">
                <p className="text-sm text-gray-600">{selectedMessage.content}</p>
              </div>
              <Textarea
                id="custom-response"
                rows={4}
                placeholder="Type your response..."
                defaultValue={draftResponses[selectedMessage.id] || ''}
              />
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setSelectedMessage(null)}>
                  Cancel
                </Button>
                <Button 
                  className="bg-teal-600 hover:bg-teal-700"
                  onClick={() => {
                    const response = document.getElementById('custom-response').value;
                    sendCustomResponse(selectedMessage.id, response);
                  }}
                >
                  <Send className="h-4 w-4 mr-2" />
                  Send Response
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default SmartTriageDashboard;
